import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Star, Search, MessageCircle, ShieldCheck, Zap, CheckCircle, XCircle, Crown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import api from '@/lib/api';
import { toast } from 'sonner';
import { isUcanApp, isApplePlatform, sendUcanNativeMessage } from '@/lib/ucanApp';

const Premium = () => {
  const [processingPayment, setProcessingPayment] = useState(false);
  const [plan, setPlan] = useState(null);

  const handleSubscribe = async () => {
    if (processingPayment) return;

    if (!plan?.plan_id) {
      toast.error('No se pudo cargar el plan Premium');
      return;
    }

    // --------------------------------------------------------
    // iOS APP
    // --------------------------------------------------------
    // Dentro de la app no usamos Mercado Pago.
    // App.tsx recibe este mensaje y abre Apple IAP.
    if (isUcanApp()) {
      if (!sendUcanNativeMessage({
          type: 'APPLE_SUBSCRIPTION',
          plan_id: plan.plan_id,
          user_type: 'client',
        })) {
        toast.error('No se pudo conectar con Apple. Cierra y vuelve a abrir la aplicación U-CAN.');
      }

      return;
    }

    // --------------------------------------------------------
    // WEB APPLE
    // --------------------------------------------------------
    // En iPhone, iPad y Mac nunca ofrecemos checkout externo.
    if (isApplePlatform()) {
      toast.info('Suscríbete con Apple desde la app U-CAN para iOS.');
      return;
    }

    // --------------------------------------------------------
    // ANDROID / WINDOWS / LINUX WEB
    // --------------------------------------------------------
    setProcessingPayment(true);

    try {
      const response = await api.post(
        '/subscription/create-payment',
        {
          plan_id: plan.plan_id
        }
      );

      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
        return;
      }

      if (response.data.sandbox_url) {
        window.location.href = response.data.sandbox_url;
        return;
      }

      toast.error('No se pudo iniciar el pago');
    } catch (error) {
      toast.error(
        error.response?.data?.detail ||
        'Error al iniciar Mercado Pago'
      );
    } finally {
      setProcessingPayment(false);
    }
  };

  const navigate = useNavigate();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });

    api.get('/subscription/plans')
      .then(res => {
        const clientPlan = res.data.find(p => p.user_type === 'client') || res.data[0];
        setPlan(clientPlan);
      })
      .catch(() => toast.error('No se pudo cargar el plan Premium'));
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-red-50 via-white to-white">

      <section className="max-w-6xl mx-auto px-4 py-14">
        <div className="text-center max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-yellow-100 text-yellow-800 font-bold mb-5">
            <Crown className="w-5 h-5" />
            U-CAN Premium
          </div>

          <h1 className="font-montserrat text-4xl sm:text-6xl font-black text-gray-900 mb-5">
            Encuentra al cuidador ideal mucho más rápido
          </h1>

          <p className="text-lg sm:text-xl text-gray-600 mb-8">
            Con Premium tú tomas el control: puedes buscar, comparar y contactar cuidadores directamente, sin esperar propuestas.
          </p>

          <Button className="bg-[#E6202E] hover:bg-[#D31522] text-white px-8 py-6 text-lg rounded-2xl shadow-lg" onClick={handleSubscribe} disabled={processingPayment}>
                {processingPayment ? 'Procesando...' : (isApplePlatform() ? 'Suscribirme con Apple' : 'Suscribirme ahora')}
              </Button>
        </div>

        <div className="grid md:grid-cols-3 gap-5 mt-14">
          {[
            { icon: Search, title: 'Busca cuidadores', text: 'Explora perfiles y encuentra quien calce mejor con tu mascota.' },
            { icon: MessageCircle, title: 'Contacta directo', text: 'Habla con cuidadores sin esperar a que te envíen propuestas.' },
            { icon: Zap, title: 'Ahorra tiempo', text: 'Ideal si necesitas resolver rápido un paseo, cuidado o daycare.' },
          ].map(({ icon: Icon, title, text }) => (
            <div key={title} className="bg-white rounded-3xl p-6 shadow-sm border">
              <div className="w-12 h-12 rounded-2xl bg-red-50 flex items-center justify-center mb-4">
                <Icon className="w-6 h-6 text-[#E6202E]" />
              </div>
              <h3 className="font-bold text-xl mb-2">{title}</h3>
              <p className="text-gray-600">{text}</p>
            </div>
          ))}
        </div>

        
        <div className="mt-14 grid md:grid-cols-2 gap-6">
          <div className="bg-white rounded-3xl p-7 shadow-sm border">
            <div className="mb-6">
              <p className="text-sm font-bold text-gray-400 uppercase tracking-wide mb-1">Para empezar</p>
              <h2 className="font-montserrat text-3xl font-black text-gray-900">Cuenta gratis</h2>
            </div>

            <div className="space-y-4">
              {['Publicar solicitudes', 'Recibir propuestas', 'Elegir cuidador'].map(item => (
                <p key={item} className="flex items-center gap-3 text-gray-800">
                  <CheckCircle className="w-5 h-5 text-green-600 shrink-0" /> {item}
                </p>
              ))}
              {['Buscar cuidadores directo', 'Contactar sin esperar', 'Ver más información'].map(item => (
                <p key={item} className="flex items-center gap-3 text-gray-400">
                  <XCircle className="w-5 h-5 shrink-0" /> {item}
                </p>
              ))}
            </div>
          </div>

          <div className="relative rounded-3xl p-[2px] bg-red-100 shadow-sm">
            <div className="h-full bg-white rounded-[22px] p-7">
              <div className="flex items-start justify-between gap-4 mb-6">
                <div>
                  <p className="text-sm font-black text-[#E6202E] uppercase tracking-wide mb-1">Más control</p>
                  <h2 className="font-montserrat text-3xl font-black text-gray-900 flex items-center gap-2">
                    <Star className="w-7 h-7 text-yellow-500 fill-yellow-400" />
                    Premium
                  </h2>
                </div>
                
              </div>

              <div className="space-y-4 mb-7">
                {[
                  'Publicar solicitudes',
                  'Recibir propuestas',
                  'Buscar cuidadores directamente',
                  'Contactar sin esperar propuestas',
                  'Ver más información de cada perfil',
                  'Más control para elegir cuidador'
                ].map(item => (
                  <p key={item} className="flex items-center gap-3 text-gray-900 font-medium">
                    <CheckCircle className="w-5 h-5 text-[#E6202E] shrink-0" /> {item}
                  </p>
                ))}
              </div>
            </div>
          </div>
        </div>

<div className="mt-14 grid md:grid-cols-2 gap-8 items-center">
          <div>
            <h2 className="font-montserrat text-3xl font-black mb-4">¿Cómo funciona?</h2>
            <p className="text-gray-600 mb-4">
              Gratis puedes publicar tu solicitud y esperar propuestas de cuidadores interesados.
            </p>
            <p className="text-gray-600">
              Con Premium, además puedes buscar tú mismo, revisar perfiles y contactar directamente al cuidador que más te guste.
            </p>
          </div>

          <div className="bg-gray-900 text-white rounded-3xl p-8">
            <p className="text-gray-300 mb-2">Plan cliente Premium</p>
            <div className="text-5xl font-black mb-2">$9.990</div>
            <p className="text-gray-300 mb-6">mensual</p>
            <Button className="w-full bg-[#E6202E] hover:bg-[#D31522] py-6 text-lg rounded-2xl" onClick={handleSubscribe} disabled={processingPayment}>
                {processingPayment ? 'Procesando...' : (isApplePlatform() ? 'Suscribirme con Apple' : 'Suscribirme ahora')}
              </Button>

          <button
            type="button"
            onClick={() => navigate(-1)}
            className="mt-5 text-gray-500 hover:text-[#E6202E] transition-colors font-medium"
          >
            ← Volver
          </button>

          </div>
        </div>

        <div className="mt-14 bg-white rounded-3xl p-7 shadow-sm border">
          <h2 className="font-bold text-2xl mb-4">Preguntas rápidas</h2>
          <div className="space-y-4 text-gray-600">
            <p><strong className="text-gray-900">¿Es obligatorio ser Premium?</strong><br />No. Puedes usar U-CAN gratis publicando solicitudes.</p>
            <p><strong className="text-gray-900">¿Qué gano con Premium?</strong><br />Puedes buscar y contactar cuidadores directamente.</p>
            <p><strong className="text-gray-900">¿Puedo cancelar después?</strong><br />Sí, puedes administrar tu suscripción desde tu panel.</p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Premium;
